class HCFsb {
  constructor(fsb = { selector: "#hc-fsb", events: [], placeholder: "(x)" }) {
    this.validateConfig(fsb);
    this.initializeProperties(fsb);
    this.attachCheckoutEvent();
    this.attachEvents();
  }

  validateConfig(fsb) {
    if (!fsb.selector || typeof fsb.selector !== "string") {
      throw new TypeError("Please pass a valid selector");
    }
  }

  initializeProperties(fsb) {
    this.selector = fsb.selector;
    this.fsbElementContainer = document.querySelector(this.selector);
    this.fsbElement = this.fsbElementContainer?.querySelector(".fsb");

    if (!this.fsbElement) {
      throw new TypeError("Freeshipping Bar Element Not Found");
    }

    this.events = fsb.events;
    this.placeholder = fsb.placeholder;
    this.shippingGoal = parseInt(this.fsbElement.dataset.shippingGoal);
    this.maxGoal = parseInt(this.fsbElement.dataset.maxGoal) || this.shippingGoal;
    this.startHeadline = this.fsbElement.dataset.headlineStart;
    this.endHeadline = this.fsbElement.dataset.headlineEnd;

    this.middleHeadlines = [];
    this.freeGifts = [];

    for (let i = 1; i <= 3; i++) {
      const goal = parseInt(this.fsbElement.dataset[`freeGift${i}Goal`]);
      const product = parseInt(this.fsbElement.dataset[`freeGift${i}`]);
      const title = this.fsbElement.dataset[`freeGiftTitle${i}`];
      const headline = this.fsbElement.dataset[`headlineMiddle${i}`];

      if (goal && product && title && headline) {
        this.freeGifts.push({ goal, product, title });
        this.middleHeadlines.push(headline);
      }
    }

    this.fsText = this.fsbElement.dataset.fsText;
  }

  async getCart() {
    try {
      const response = await fetch("/cart.js");
      return await response.json();
    } catch (error) {
      console.error("Error fetching cart:", error);
      throw error;
    }
  }

  getBaseTotal(cart) {
    const giftIds = this.freeGifts.map((g) => g.product);
    const excludedHandles = [
      "delivery-fee-hk-island",
      "delivery-fee-kowloon",
      "express-checkout",
      "express-delivery-kowloon",
    ];
    return cart.items.reduce((sum, item) => {
      if (giftIds.includes(item.id) || excludedHandles.includes(item.handle)) {
        return sum;
      }
      return sum + item.line_price;
    }, 0);
  }

  async update() {
    try {
      const cart = await this.getCart();
      const baseTotal = this.getBaseTotal(cart);

      let changed = false;

      for (const gift of this.freeGifts) {
        const inCart = this.isProductInCart(cart, gift.product);
        const eligible = baseTotal >= gift.goal;

        if (eligible && !inCart) {
          await this.addFreeGift(gift.product);
          changed = true;
        } else if (!eligible && inCart) {
          await this.removeFreeGift(gift.product);
          changed = true;
        }
      }

      if (changed) {
        location.reload();
        return;
      }

      this.updateProgressBar(cart);
      const virtualCart = { ...cart, total_price: baseTotal };
      this.updateHeadline(virtualCart);
    } catch (error) {
      console.error("Error updating FSB:", error);
    }
  }

  updateProgressBar(cart) {
    if (!this.maxGoal || this.maxGoal <= 0) {
      return;
    }
    let progress = (cart.total_price * 100) / this.maxGoal;
    if (progress > 100) progress = 100;
    if (progress < 0) progress = 0;
    this.fsbElement.style.setProperty("--progress", `${progress}%`);
  }

  updateHeadline(cart) {
    const headline = this.fsbElementContainer.querySelector(".fsb-paragraph");

    if (cart.total_price > 0 && cart.total_price < this.shippingGoal) {
      const shippingDifference = this.shippingGoal - cart.total_price;
      headline.innerHTML = this.startHeadline
        .replace(
          this.placeholder,
          `<span class="fsb--number">${this.formatMoney(
            shippingDifference
          )}</span>`
        )
        .replace("(goal)", this.fsText);
      return;
    }

    if (this.freeGifts.length === 0) {
      headline.innerHTML = this.endHeadline || "Free Shipping Reached!";
      return;
    }

    for (let i = 0; i < this.freeGifts.length; i++) {
      const gift = this.freeGifts[i];
      const difference = gift.goal - cart.total_price;

      if (cart.total_price < gift.goal) {
        headline.innerHTML = this.middleHeadlines[i]
          .replace(
            this.placeholder,
            `<span class="fsb--number">${this.formatMoney(difference)}</span>`
          )
          .replace("(item)", gift.title);
        return;
      }
    }

    const lastGift = this.freeGifts[this.freeGifts.length - 1];
    if (cart.total_price >= lastGift.goal) {
      headline.innerHTML = this.endHeadline.replace("(item)", lastGift.title);
    }
  }

  attachEvents() {
    if (this.events.length > 0) {
      this.events.forEach((event) => {
        document.addEventListener(event, () => this.update(), true);
      });
    }
  }

  formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const value = "";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || "${{amount_no_decimals}}";

    const formatWithDelimiters = (
      number,
      precision = 2,
      thousands = ",",
      decimal = "."
    ) => {
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split(".");
      const dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`
      );
      return dollars + (parts[1] ? decimal + parts[1] : "");
    };

    const moneyFormat = formatString.match(placeholderRegex)?.[1];
    return formatString.replace(
      placeholderRegex,
      formatWithDelimiters(cents, moneyFormat === "amount_no_decimals" ? 0 : 2)
    );
  }

  async addFreeGift(product) {
    const formData = { items: [{ id: product, quantity: 1 }] };
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        throw new Error("Error adding gift product");
      }
      console.log("Gift product added:", product);
    } catch (error) {
      console.error("Error adding gift:", error);
    }
  }

  async removeFreeGift(product) {
    const updates = { [product]: 0 };
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) {
        throw new Error("Error removing gift product");
      }
      console.log("Gift product removed:", product);
    } catch (error) {
      console.error("Error removing gift:", error);
    }
  }

  isProductInCart(cart, productId) {
    return cart.items.some((item) => item.id === productId);
  }

  attachCheckoutEvent() {
    if (window.Shopify && Shopify.designMode) {
      return;
    }

    if (!this.freeGifts.length) {
      return;
    }

    const checkoutBtn = document.querySelector("[name='checkout']");
    if (checkoutBtn) {
      checkoutBtn.addEventListener(
        "click",
        async (event) => {
          event.preventDefault();
          await this.handleCheckoutProcess(checkoutBtn);
        },
        { capture: true, once: true }
      );
    }
  }

  hasZapietCheckoutManager() {
    return !!(
      document.querySelector(".zapiet-widget-wrapper") ||
      document.querySelector("#zapiet-checkout-methods") ||
      document.querySelector("[data-zapiet-checkout-button]") ||
      document.querySelector("[class*='zapiet-widget']")
    );
  }

  async handleCheckoutProcess(checkoutBtn) {
    const cart = await this.getCart();
    const baseTotal = this.getBaseTotal(cart);
    for (const gift of this.freeGifts) {
      if (
        baseTotal >= gift.goal &&
        !this.isProductInCart(cart, gift.product)
      ) {
        location.reload();
        return;
      }
    }

    if (checkoutBtn.form && typeof checkoutBtn.form.requestSubmit === "function") {
      checkoutBtn.form.requestSubmit(checkoutBtn);
      return;
    }

    checkoutBtn.form.submit();
  }
}
